import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { styled } from '../../stitches.config';
import { Icon } from '../Icon';

export const StyledBreadcrumb = styled('nav', {});
export const StyledBreadcrumbList = styled('ol', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  wordBreak: 'break-word',
  fontSize: '$sm',
  color: '$blue-600',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  mediaSm: {
    gap: 10,
  },
});

export const StyledBreadcrumbItem = styled('li', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
});

export const StyledBreadcrumbLink = styled('span', {
  '& > a.betterleap-ui-breadcrumb-link': {
    fontFamily: '$body',
    transition: 'color 0.2s ease-in-out',
    textDecoration: 'none',
    color: '$blue-600',
    '&:hover': {
      color: '$blue-700',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
});

export const StyledBreadcrumbPage = styled('span', {
  fontWeight: 'normal',
  color: '$blue-600',
  fontFamily: '$body',
});

export const StyledBreadcrumbSeparator = styled('li', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export interface BreadcrumbProps
  extends React.ComponentProps<typeof StyledBreadcrumb> {}
export interface BreadcrumbListProps
  extends React.ComponentProps<typeof StyledBreadcrumbList> {}
export interface BreadcrumbItemProps
  extends React.ComponentProps<typeof StyledBreadcrumbItem> {}
export interface BreadcrumbLinkProps
  extends Omit<React.ComponentProps<typeof StyledBreadcrumbLink>, 'ref'> {
  asChild?: boolean;
  ref?: React.Ref<HTMLAnchorElement>;
}
export interface BreadcrumbPageProps
  extends React.ComponentProps<typeof StyledBreadcrumbPage> {}
export interface BreadcrumbSeparatorProps
  extends React.ComponentProps<typeof StyledBreadcrumbSeparator> {}

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  ({ ...rest }, ref) => (
    <StyledBreadcrumb ref={ref} aria-label='breadcrumb' {...rest} />
  ),
);

Breadcrumb.displayName = 'Breadcrumb';

export const BreadcrumbList = forwardRef<HTMLOListElement, BreadcrumbListProps>(
  ({ ...props }, ref) => <StyledBreadcrumbList ref={ref} {...props} />,
);

BreadcrumbList.displayName = 'BreadcrumbList';

export const BreadcrumbItem = forwardRef<HTMLLIElement, BreadcrumbItemProps>(
  ({ ...props }, ref) => <StyledBreadcrumbItem ref={ref} {...props} />,
);

BreadcrumbItem.displayName = 'BreadcrumbItem';

export const BreadcrumbLink = forwardRef<
  HTMLAnchorElement,
  BreadcrumbLinkProps
>(({ asChild, css, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <StyledBreadcrumbLink
      css={{
        '& > a.betterleap-ui-breadcrumb-link': {
          ...css,
        },
      }}
    >
      <Comp ref={ref} className='betterleap-ui-breadcrumb-link' {...props} />
    </StyledBreadcrumbLink>
  );
});

BreadcrumbLink.displayName = 'BreadcrumbLink';

export const BreadcrumbPage = forwardRef<HTMLSpanElement, BreadcrumbPageProps>(
  ({ ...props }, ref) => (
    <StyledBreadcrumbPage
      ref={ref}
      role='link'
      aria-disabled='true'
      aria-current='page'
      {...props}
    />
  ),
);

BreadcrumbPage.displayName = 'BreadcrumbPage';

export const BreadcrumbSeparator = ({
  children,
  ...props
}: BreadcrumbSeparatorProps) => (
  <StyledBreadcrumbSeparator role='presentation' aria-hidden='true' {...props}>
    {children ?? <Icon name='chevron-right' size={18} color='$blue-600' />}
  </StyledBreadcrumbSeparator>
);

BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';
